import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { analyticsApi, studentApi } from '@/api';
import { GreetingBanner, RailCard, todayLine } from '@/components/shell';
import {
  Alert,
  Badge,
  BoxAvatar,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
} from '@/components/ui';
import { APPLICATION_TONES, STUDENT_APPLICATION_LABELS } from '@/features/applications/status';
import { PIPELINE_SERIES } from '@/features/analytics/series';
import { TrendChart } from '@/features/analytics/TrendChart';
import { interviewWhen } from '@/features/interviews/status';
import { useAuth } from '@/hooks/useAuth';
import { PortfolioLinkBar } from '@/features/student/PortfolioLinkBar';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatDate, relativeTime } from '@/utils/format';

/** The Application analytics page's default period. */
const TREND_DAYS = 90;

const FIGURES = [
  {
    key: 'saved_internships',
    label: 'Saved',
    icon: 'bookmark',
    to: paths.student.saved,
    tint: 'bg-brand-50 text-brand-700',
    edge: 'bg-brand-500',
  },
  {
    key: 'applications',
    label: 'Applied',
    icon: 'application',
    to: paths.student.applications,
    tint: 'bg-info-50 text-info-700',
    edge: 'bg-info-500',
  },
  {
    key: 'interviews',
    label: 'Interviews',
    icon: 'calendar',
    to: paths.student.interviews,
    tint: 'bg-warning-50 text-warning-800',
    edge: 'bg-warning-500',
  },
  {
    key: 'offers',
    label: 'Offers',
    icon: 'check',
    to: paths.student.offers,
    tint: 'bg-success-50 text-success-700',
    edge: 'bg-success-500',
  },
];

/** The small icon tile that starts a row in a dashboard card. */
function RowIcon({ name, tone = 'brand' }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-lg ring-1 ring-inset',
        tone === 'info'
          ? 'bg-info-50 text-info-700 ring-info-100'
          : 'bg-brand-50 text-brand-600 ring-brand-100',
      )}
    >
      <Icon name={name} size={17} />
    </span>
  );
}

export function StudentDashboardPage() {
  const { user } = useAuth();

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['student-dashboard'],
    queryFn: studentApi.getDashboard,
  });

  // Shared with the profile page, so this is normally already cached.
  const { data: profile } = useQuery({
    queryKey: ['student-profile'],
    queryFn: studentApi.getProfile,
  });

  const trend = useQuery({
    queryKey: ['student-insights', TREND_DAYS],
    queryFn: () => analyticsApi.getStudentInsights(TREND_DAYS),
  });

  if (isPending) return <LoadingState label="Loading your dashboard…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  // No skills to match on and no field stated: every ranking this student sees
  // is arbitrary, and "complete your profile" is advice they cannot act on yet.
  // Asking what they want is.
  const isFresher = profile?.skills.length === 0 && !profile.target_job_category;

  const counters = data.counters;
  // // The four counts have no ceiling of their own, so the rings are drawn as
  // // fractions of the largest of them. That makes the set comparative -- you
  // // can see the pipeline narrowing -- rather than four full circles that
  // // encode nothing.
  // const peak = Math.max(
  //   counters.saved_internships,
  //   counters.applications,
  //   counters.interviews,
  //   counters.offers,
  //   1,
  // );

  return (
    <div>
      <GreetingBanner
        // name={user?.full_name?.split(' ')[0] ?? 'there'}
        name={user?.full_name ?? 'there'}
        subtitle={todayLine()}
        className="shell-hero-night"
        action={
          <LinkButton to={paths.internships} variant="light">
            Find internships
          </LinkButton>
        }
      />

      <PortfolioLinkBar className="mt-6" />

      {isFresher && (
        <Card className="mt-6">
          <CardHeader
            title="Just starting out?"
            description="Nothing to list yet is normal, and it is not what we need from you first."
            action={
              <LinkButton to={paths.student.career} size="sm">
                Plan my path
              </LinkButton>
            }
          />
          <p className="text-ink-muted text-sm">
            Tell us what you want to do and we will show you what open internships in that field are
            actually asking for, plus the roles you could apply to now.
          </p>
        </Card>
      )}

      {!data.university_email_verified && (
        <Alert tone="info" className="mt-6" title="Verify your university email">
          Companies trust verified student profiles.{' '}
          <Link to={paths.student.profile} className="font-medium underline">
            Add and verify it
          </Link>
          .
        </Alert>
      )}

      <section aria-label="Your pipeline" className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {FIGURES.map((figure) => (
          <Link key={figure.key} to={figure.to} className="group rounded-card">
            <Card className="relative h-full overflow-hidden text-center transition group-hover:-translate-y-0.5 group-hover:shadow-md">
              <span aria-hidden="true" className={`${figure.edge} absolute inset-x-0 top-0 h-1`} />
              <span
                aria-hidden="true"
                className={`${figure.tint} mx-auto mt-1 grid size-11 place-items-center rounded-full`}
              >
                <Icon name={figure.icon} size={20} />
              </span>
              <p className="text-ink mt-3 text-3xl font-bold tabular-nums">
                {counters[figure.key]}
              </p>
              <p className="text-ink-muted mt-1 text-sm">{figure.label}</p>
            </Card>
          </Link>
        ))}
      </section>

      {/* Feed on the left, standing figures on the right -- the reference's
          split. Below `lg` the rail drops under the feed rather than beside
          it, because 320px of cards in a phone-width column is unreadable. */}
      <div className="mt-6 grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader
              title="Your applications"
              description="The internships you applied to most recently."
              action={
                <LinkButton to={paths.student.applications} variant="secondary" size="sm">
                  All applications
                </LinkButton>
              }
            />
            {data.recent_applications?.length ? (
              <ul className="divide-line divide-y">
                {data.recent_applications.map((application) => (
                  <li
                    key={application.id}
                    className="hover:bg-surface-muted -mx-2 flex flex-wrap justify-between gap-4 rounded-lg px-2 py-3 transition"
                  >
                    <div className="flex min-w-0 flex-1 basis-56 items-center gap-3">
                      {/* <RowIcon name="application" /> */}
                      <BoxAvatar name={application.company_name} />
                      <div className="min-w-0">
                        <Link
                          to={paths.student.applicationDetail(application.id)}
                          className="text-ink hover:text-brand-700 text-sm font-medium"
                        >
                          {application.title}
                        </Link>
                        <p className="text-ink-muted text-xs">
                          {application.company_name} · applied{' '}
                          {formatDate(application.submitted_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge tone={APPLICATION_TONES[application.status] ?? 'neutral'} dot>
                        {STUDENT_APPLICATION_LABELS[application.status] ?? application.status}
                      </Badge>
                      {/* How long it has sat there. A status with no age behind
                          it does not answer the question a student actually has. */}
                      <p className="text-ink-muted mt-1 text-xs">
                        Updated {relativeTime(application.status_changed_at)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                compact
                icon={<Icon name="application" size={16} />}
                title="No applications yet"
                description="Apply to an internship and it shows up here with its status."
                action={
                  <LinkButton to={paths.internships} variant="secondary" size="sm">
                    Browse internships
                  </LinkButton>
                }
              />
            )}
          </Card>

          {data.upcoming_interviews?.length > 0 && (
            <Card>
              <CardHeader
                title="Your next interviews"
                action={
                  <LinkButton to={paths.student.interviews} variant="secondary" size="sm">
                    All interviews
                  </LinkButton>
                }
              />
              <ul className="divide-line divide-y">
                {data.upcoming_interviews.map((interview) => (
                  <li key={interview.id} className="flex flex-wrap justify-between gap-4 py-3">
                    <div className="flex min-w-0 flex-1 basis-56 items-start gap-3">
                      <RowIcon name="calendar" tone="info" />
                      <div className="min-w-0">
                        <p className="text-ink text-sm font-medium">{interview.title}</p>
                        <p className="text-ink-muted text-xs">
                          {interview.company_name} · {interview.internship_title}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-ink text-sm">{interviewWhen(interview)}</p>
                      {interview.needs_response && (
                        <Badge tone="warning" dot className="mt-1">
                          Needs your reply
                        </Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <aside className="space-y-4">
          <RailCard
            icon="calendar"
            title="Upcoming deadlines"
            subtitle="Offers to answer, edits to make, saved listings closing"
            links={[{ to: paths.student.saved, label: 'All saved' }]}
          >
            {data.upcoming_deadlines.length === 0 ? (
              <EmptyState
                compact
                icon={<Icon name="bookmark" size={16} />}
                title="Nothing due. Save an internship to track its deadline here."
              />
            ) : (
              <ul className="divide-line divide-y">
                {data.upcoming_deadlines.map((deadline) => (
                  <li
                    key={`${deadline.kind}-${deadline.internship_id}-${deadline.application_deadline}`}
                    className="flex justify-between gap-3 py-2"
                  >
                    <div className="min-w-0">
                      <Link
                        to={deadline.link ?? paths.internshipDetail(deadline.internship_id)}
                        className="text-ink block truncate text-xs font-medium hover:underline"
                      >
                        {deadline.title}
                      </Link>
                      <p className="text-ink-muted truncate text-[11px]">
                        <span
                          className={
                            deadline.kind === 'APPLY'
                              ? 'text-brand-700 font-medium'
                              : 'text-warning-700 font-medium'
                          }
                        >
                          {deadline.action ?? 'Apply'}
                        </span>{' '}
                        · {deadline.company_name}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      {/* <p className="text-ink text-xs font-semibold tabular-nums">
                        {deadline.days_remaining}d
                      </p> */}
                      <Badge tone={deadline.days_remaining <= 3 ? 'danger' : 'warning'}>
                        {deadline.days_remaining}d
                      </Badge>
                      <p className="text-ink-muted text-[11px]">
                        {formatDate(deadline.application_deadline)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </RailCard>
        </aside>
      </div>

      {/* The chart from Application analytics, over its default 90 days and
          from the same cache. Quietly absent if it cannot load: the page
          itself reports the error properly. */}
      {trend.data && (
        <Card className="mt-6">
          <CardHeader
            title="Activity over time"
            description="When you applied, and the interviews, offers and placements that followed."
            action={
              <LinkButton to={paths.student.insights} variant="secondary" size="sm">
                Full analytics
              </LinkButton>
            }
          />
          <TrendChart
            points={trend.data.trend}
            series={PIPELINE_SERIES}
            granularity={trend.data.period?.granularity}
            emptyLabel="You have not applied in this period"
          />
        </Card>
      )}
    </div>
  );
}

// Taken off the dashboard at the client's request. Kept so they can go back.
// Also needed to bring these back: ProgressBar and StatRing, and `peak` above.
//
// -- Application updates (was under "Your next interviews")
//           <Card>
//             <CardHeader
//               title="Application updates"
//               description="What companies have done with your applications."
//             />
//             {data.application_timeline?.length ? (
//               <ol className="before:bg-line relative space-y-4 pl-6 before:absolute before:top-1 before:bottom-1 before:left-2 before:w-px">
//                 {data.application_timeline.map((event, index) => (
//                   <li key={`${event.application_id}-${index}`} className="relative">
//                     <span
//                       aria-hidden="true"
//                       className="bg-brand-500 ring-surface absolute top-1 -left-[21.5px] size-3 rounded-full ring-4"
//                     />
//                     <div className="min-w-0">
//                       <p className="text-ink text-sm">
//                         <Link
//                           to={paths.student.applicationDetail(event.application_id)}
//                           className="font-medium hover:underline"
//                         >
//                           {event.title}
//                         </Link>{' '}
//                         <span className="text-ink-muted">
//                           {/* The first entry has nothing to move from, and
//                               "moved to submitted" describes the student's own
//                               act as though a company had performed it. */}
//                           {event.from_status
//                             ? `moved to ${(STUDENT_APPLICATION_LABELS[event.to_status] ?? event.to_status).toLowerCase()}`
//                             : 'application sent'}
//                         </span>
//                       </p>
//                       <p className="text-ink-muted text-xs">
//                         {event.company_name} · {relativeTime(event.created_at)}
//                       </p>
//                     </div>
//                   </li>
//                 ))}
//               </ol>
//             ) : (
//               <EmptyState
//                 compact
//                 icon={<Icon name="clockOutline" size={16} />}
//                 title="Nothing has moved yet."
//                 description="Companies update applications as they review them."
//               />
//             )}
//           </Card>
//
// -- Your pipeline (replaced by the figures across the top)
//           <RailCard
//             icon="pipeline"
//             title="Your pipeline"
//             subtitle="Where everything stands"
//             links={[
//               { to: paths.student.applications, label: 'All applications' },
//               { to: paths.student.offers, label: 'View offers' },
//             ]}
//           >
//             <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 xl:grid-cols-2">
//               <StatRing value={counters.saved_internships} max={peak} label="Saved" />
//               <StatRing value={counters.applications} max={peak} label="Applied" />
//               <StatRing value={counters.interviews} max={peak} label="Interviews" />
//               <StatRing value={counters.offers} max={peak} label="Offers" tone="success" />
//             </div>
//           </RailCard>
//
// -- Profile completion
//           <RailCard
//             icon="student"
//             title="Profile completion"
//             subtitle="Ranks you in company searches"
//             links={[{ to: paths.student.profile, label: 'Edit profile' }]}
//           >
//             <ProgressBar value={data.profile_completion} label="Completed" />
//             {data.profile_sections_missing.length > 0 ? (
//               <ul className="mt-3 space-y-1.5">
//                 {data.profile_sections_missing.map((section) => (
//                   <li key={section} className="text-ink-muted flex items-center gap-2 text-xs">
//                     <span className="size-1.5 rounded-full bg-warning-500" aria-hidden="true" />
//                     Add your {section.toLowerCase()}
//                   </li>
//                 ))}
//               </ul>
//             ) : (
//               <p className="text-ink-muted mt-3 text-xs">Your profile is complete. Nice work.</p>
//             )}
//           </RailCard>
//
// -- Invite classmates (needs InviteCard from @/features/referrals/InviteCard)
//           <InviteCard variant="rail" />
//
// -- Recent activity
//           <RailCard icon="clock" title="Recent activity" subtitle="What you have done">
//             {data.recent_activity.length === 0 ? (
//               <EmptyState compact icon={<Icon name="clock" size={16} />} title="Nothing yet." />
//             ) : (
//               <ul className="divide-line divide-y">
//                 {data.recent_activity.map((activity, index) => (
//                   <li
//                     key={`${activity.action}-${index}`}
//                     className="flex justify-between gap-3 py-2 text-xs"
//                   >
//                     <span className="text-ink min-w-0 truncate">{activity.action}</span>
//                     <span className="text-ink-muted shrink-0">
//                       {relativeTime(activity.created_at)}
//                     </span>
//                   </li>
//                 ))}
//               </ul>
//             )}
//           </RailCard>
